import { MCard, MGrid, MSpinner } from "components/Shared";
import { FormUsageDetail, PhoenixFormDetail, PhoenixFormDetailEdit } from "interfaces/Mueller.Business.Models";
import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { getFormDefinition, getFormDetails, saveFormDetails, updateFormDefinition } from "requests/phoenixForms";
import { userHasRight } from "selectors/rights";
import { FORM_DESIGN_EDIT, FORM_MAINTENANCE_EDIT, FORM_ERROR_REPORT_CREATE } from "typeConstants/rights";
import { connect } from "react-redux";
import { ACTION_DOWNLOAD, MGridColumnDefinition } from "components/Shared/MGrid";
import { DATA_TYPE_BOOLEAN, DATA_TYPE_DATE, DATA_TYPE_JSX, DATA_TYPE_MULTI_LINE_STRING, DATA_TYPE_NUMBER } from "typeConstants/dataTypes";
import { KeyValueOriginal, MCardAction, MCardField } from "components/Shared/MCard";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { Button } from "react-md";
import PhoenixFormPreview from "./PhoenixFormPreview";
import WebConfig from "middlewares/WebConfig";
import { withRouter } from "react-router-dom";
import CodeEditor from "./Designer/CodeEditor/CodeEditor";
import LoadingCover from "components/Shared/LoadingCover";
import { CreatePdfDefinitionRequest, FormDefinition, PdfDisplayDefinition } from "interfaces/Phoenix.FormsDesigner.Models";
import { PhoenixCaseFormOrPreview, usePhoenixForms } from "../PhoenixFormsProvider";
import { webApiDownloadFile } from "tools/fileDownloadTools";
import GenerateErrorReportDialog from "./GenerateErrorReportDialog";
import { createPdfDefinition } from "requests/phoenixPdf";

interface Props {
  pnxFormId: string;
  availFormId: number;
  onSaveFinished: () => void;
  onCopyFormClicked: () => void;

  // Redux
  canEditFormInfo: boolean;
  canEditFormDesign: boolean;
  canGenerateErrorReport: boolean;
  history: any; // from router
}

const PhoenixFormsListDetails = ({ pnxFormId, availFormId, canEditFormInfo, canEditFormDesign, canGenerateErrorReport, onSaveFinished, onCopyFormClicked, history }: Props) => {
  const mounted = useRef(true);
  const phoenixFormIdOrAvailFormId = pnxFormId ?? (availFormId + "");
  const [formDetails, setFormDetails] = useState<PhoenixFormDetail>();
  const [errorReportDialogVisible, setErrorReportDialogVisible] = useState<boolean>(false);

  const isPnxForm = !!pnxFormId;
  const dialog = useContext<DialogProviderValue>(DialogContext);
  const { getFormData } = usePhoenixForms();

  useEffect(() => () => { mounted.current = false; }, []);

  const reloadFormDetails = () => {
    setFormDetails(null);
    getFormDetails(phoenixFormIdOrAvailFormId, setFormDetails, mounted);
  };

  useEffect(reloadFormDetails, [pnxFormId, availFormId]);

  if (!formDetails) return <MSpinner />;

  const onSave = (changeArray: KeyValueOriginal<PhoenixFormDetail>[]) => {
    let postData: Partial<PhoenixFormDetailEdit> = {};

    // post data has the same fields as the data model, and also has a field to track if the value was changed
    changeArray.forEach(({ key, value }) => {
      postData[key] = value;
      postData[key + "Changed"] = true;
    });

    setFormDetails(null);
    saveFormDetails(phoenixFormIdOrAvailFormId, postData, () => {
      reloadFormDetails();
      onSaveFinished?.();
    }, mounted);
  };

  const gridColDefs: MGridColumnDefinition<FormUsageDetail>[] = [
    {
      key: "customerNameNumber",
      name: "Customer Name/Number",
      dataSource: (row) => `${row.customerName ?? "(Unnamed)"} (${row.customerNumber})`,
      width: 300,
    },
    {
      key: "surveyType",
      name: "Survey Type",
      dataSource: "surveyType",
      width: 200,
    },
    {
      key: "customerInactiveDate",
      name: "Customer Inactive Date",
      dataSource: "customerInactiveDate",
      dataType: DATA_TYPE_DATE,
      width: 175,
    },
    {
      key: "surveyInactiveDate",
      name: "Survey Type Inactive Date",
      dataSource: "surveyInactiveDate",
      dataType: DATA_TYPE_DATE,
      width: 190,
    },
    {
      key: "volume",
      name: "30 Day Volume",
      dataSource: "volume",
      width: 125,
      dataType: DATA_TYPE_NUMBER,
    },
  ];

  const changePublicPrivate = async (setPublic: boolean) => {
    const inContainer = (node: ReactNode) => <div className="pnx-form-list-dialog-public-private">{node}</div>;
    const editingTitle = `Mark form ${setPublic ? "Public" : "Private"}?`;
    const dialogProps = { className: "dialog-widthfix" };

    const doTheThing = await new Promise<boolean>(resolve => dialog.showYesNo(
      editingTitle,
      `Are you sure you want to set this form ${setPublic ? "Public" : "Private"}?`,
      () => resolve(true), () => resolve(false)
    ));

    if (doTheThing) {
      dialog.show(
        editingTitle,
        inContainer(<LoadingCover loadingMessages={["Saving form definition..."]} />),
        null,
        dialogProps,
      );

      let formDef = await new Promise<FormDefinition>(resolve => getFormDefinition(pnxFormId, resolve, () => resolve(null), mounted));

      if (formDef) {
        formDef.publicPreviewEnabled = setPublic;  
      }

      try {
        await new Promise<FormDefinition>((resolve, reject) => updateFormDefinition(pnxFormId, formDef, resolve, reject, mounted));
        reloadFormDetails();
        dialog.hide();
      } catch {
        dialog.showOk(editingTitle, inContainer("Updating form definition failed, please try again"));
      }
    }
  };

  let cardFields = [
    { editable: false, key: "name", label: "Form Name", value: formDetails.name },
    isPnxForm && { editable: true, key: "category", label: "Category", value:  formDetails.category },
    isPnxForm && { editable: true, key: "description", label: "Description", value: formDetails.description },
    isPnxForm && { editable: false, key: "modifiedDate", label: "Last Update", value: formDetails.modifiedDate, dataType: DATA_TYPE_DATE },
    isPnxForm && { editable: false, key: "modifiedBy", label: "Updated By", value: formDetails.modifiedBy, dataType: DATA_TYPE_DATE },
    isPnxForm && { editable: false, key: "publishDate", label: "Publish Date", value: formDetails.publishDate, dataType: DATA_TYPE_DATE },
    { editable: false, key: "volume30", label: "30 day volume", value: formDetails.volume30 },
    isPnxForm && { editable: false, key: "phoenixPdfDefinitionIds", label: "Phoenix PDF", value: formDetails.phoenixPdfDefinitionIds?.length > 0, dataType: DATA_TYPE_BOOLEAN },
    { editable: true, key: "notes", label: "Export", value: formDetails.notes },
    formDetails.availFormTypeName === "Sketch" && { editable: true, key: "cmsTopicKey", label: "Content Code", value: formDetails.cmsTopicKey },
  ].filter(f => !!f) as MCardField[];

  if (isPnxForm) {
    if (canEditFormDesign) {
      let publicPrivateButton: ReactNode;

      if (formDetails.publicPreviewEnabled) {
        publicPrivateButton = <Button primary raised onClick={() => changePublicPrivate(false)}>Make Private</Button>;
      } else {
        publicPrivateButton = <Button primary raised onClick={() => changePublicPrivate(true)}>Make Public</Button>;
      }

      cardFields.push({ editable: false, label: "Public Preview " + (formDetails.publicPreviewEnabled ? "is enabled" : "is disabled"), value: publicPrivateButton, dataType: DATA_TYPE_JSX });
    } else {
      cardFields.push({ editable: false, label: "Public Preview Enabled", value: formDetails.publicPreviewEnabled });
    }
  }

  const onDisable = () => {
    if (formDetails.usageDetails?.length > 0) {
      dialog.showOk("Warning", "Form cannot be deactivated while in use. Please remove form from all survey types before deactivating.");
    } else {
      onSave([{ key: "inactiveDate", value: (new Date()).toISOString(), originalValue: formDetails.inactiveDate }]);
    }
  };

  const openInNewWindow = () => {
    window.open(`${WebConfig.orthosRoot}/webui/form-preview/${formDetails.phoenixFormId}`, "_blank");
  };

  const onEditFormDesignClick = () => {
    history.push(`/phoenixforms-designer/${pnxFormId}`);
  };

  const onCreatePdfClick = (newFormVals: Partial<CreatePdfDefinitionRequest> = null) => {
    if (!newFormVals)
          newFormVals = {
          category: "",
          name: "",
          description: "",
        };
  
      dialog.showYesNo(
        null,
        <MCard
          title={"New PDF"}
          fields={[
            { key: "category", label: "Category", value: newFormVals.category },
            { key: "name", label: "Name", value: newFormVals.name },
            { key: "description", label: "Description", dataType: DATA_TYPE_MULTI_LINE_STRING, value: newFormVals.description },
          ]}
          onlyEditable
          onEditChanged={kvp => kvp.forEach(({ key, value }) => newFormVals[key] = value)}
        />,
        () => {
          createPdf(newFormVals);
        },
        dialog.hide,
        "Create",
        "Cancel",
        { className: "dialog-widthfix new-form-dialog" },
      );
  }

  const createPdf = (newFormVals: Partial<CreatePdfDefinitionRequest>) => {
      createPdfDefinition(
        newFormVals as CreatePdfDefinitionRequest,
        (pdfDef: PdfDisplayDefinition) => history.push(`/phoenix-pdf-designer/${pdfDef.displayDefinitionId}`),
        mounted
      );
    };

  const onEditPDFClick = () =>{
    let pdfId = formDetails.phoenixPdfDefinitionIds[0];
    history.push(`/phoenix-pdf-designer/${pdfId}`);
  };

  const onEditTestDataModelClick = async () => {
    const editingTitle = `Editing Test Data Model for ${formDetails.name}`;
    const inContainer = (node: ReactNode) => <div className="pnx-form-list-code-editor">{node}</div>;
    const dialogProps = { className: "dialog-widthfix" };

    dialog.showOk(
      editingTitle,
      inContainer(<LoadingCover loadingMessages={["Loading Form Definition..."]} />),
      dialog.hide,
      "Cancel",
      dialogProps,
    );

    let formDef = await new Promise<FormDefinition>((resolve, reject) => getFormDefinition(pnxFormId, resolve, reject, mounted));

    let shouldSave = await new Promise<boolean>(resolve =>
      dialog.showYesNo(
        editingTitle,
        inContainer(<CodeEditor
          mode="xml"
          onChange={newValue => formDef.testDataModel = newValue}
          value={formDef.testDataModel}
        />),
        () => resolve(true),
        () => resolve(false),
        "Save",
        "Cancel",
        dialogProps,
      ));

    if (shouldSave) {
      dialog.show(
        editingTitle,
        inContainer(<LoadingCover loadingMessages={["Saving Test Data Model..."]} />),
        null,
        dialogProps,
      );

      try {
        await new Promise<FormDefinition>((resolve, reject) => updateFormDefinition(pnxFormId, formDef, resolve, reject, mounted));
        getFormData({ previewFormId: pnxFormId, isAnonymousPreview: false } as PhoenixCaseFormOrPreview, true);
        dialog.hide();
      } catch {
        dialog.showOk(editingTitle, "Saving form test data model failed, please try again");
      }
    } else {
      dialog.hide();
    }
  };

  const onExportToCsvClicked = () => {
    webApiDownloadFile(`/phoenixforms-designer/forms/${pnxFormId}/export-csv`);
  };

  if (formDetails.inactiveDate) cardFields.push({ editable: false, label: "Inactive Date", value: formDetails.inactiveDate, dataType: DATA_TYPE_DATE });
  else if (canEditFormInfo && formDetails.availFormId) cardFields.push({ key: "disableButton", control: <Button primary raised onClick={onDisable}>Deactivate</Button>, editFields: [] });
  
  const formInformationActions: MCardAction[] = [];
  const formPreviewActions: MCardAction[] = [];

  if (isPnxForm && canGenerateErrorReport) formInformationActions.push({ icon: "add_chart", helpText: "Generate Error Report", order: 100, onClick: () => setErrorReportDialogVisible(true) });

  if (formDetails.publicPreviewEnabled) formPreviewActions.push({ icon: "open_in_new", helpText: "Open in new window", onClick: openInNewWindow});
  if (canEditFormInfo) formPreviewActions.push({ icon: "tune", helpText: "Edit test data model", onClick: onEditTestDataModelClick });
  if (canEditFormDesign) {
    formPreviewActions.push({
      icon: "download", helpText: "Export to CSV", onClick: onExportToCsvClicked,
    }, {
      icon: "content_copy", helpText: "Copy Form", onClick: onCopyFormClicked,
    }, {
      icon: "edit", helpText: "Edit form design", onClick: onEditFormDesignClick,
    });

    if (formDetails.phoenixPdfDefinitionIds?.length > 0) {
      formPreviewActions.push({
        icon: "picture_as_pdf", helpText: "Edit PDF", onClick: onEditPDFClick,
      });
    }
    else {
      formPreviewActions.push({
        icon: "picture_as_pdf", helpText: "Create PDF", onClick:() => onCreatePdfClick({ formDefinitionId: formDetails.phoenixFormId, category: formDetails.category, description: formDetails.description, name: formDetails.name}),
      });
    }
  }

  // key the first card on public/private so the jsx updates
  return (<>
    <MCard
      //key={`form-details-${formDetails.publicPreviewEnabled}`} 
      title="Form Information"
      fields={cardFields}
      isEditable={canEditFormInfo}
      onEditFinished={onSave}
      actions={formInformationActions}
    />
    <MGrid
      cardStyle
      headerText="Accounts Using Form"
      className="accounts-using-form"
      columnDefinitions={gridColDefs}
      data={formDetails.usageDetails ?? []}
      actions={[{ code: ACTION_DOWNLOAD }]}
      downloadFileName={`Accounts Using Form: ${formDetails.name}`}
    />
    {isPnxForm && (
      <MCard title={`Preview for ${formDetails.name}`} renderChildrenInDiv actions={formPreviewActions}>
        <PhoenixFormPreview pnxFormId={pnxFormId} showUtilities />
      </MCard>
    )}
    {errorReportDialogVisible && <GenerateErrorReportDialog formId={pnxFormId} formName={formDetails.name} onDismiss={() => setErrorReportDialogVisible(false)} />}
  </>);
};

const mapStateToProps = state => ({
  canEditFormInfo: userHasRight(FORM_MAINTENANCE_EDIT)(state),
  canEditFormDesign: userHasRight(FORM_DESIGN_EDIT)(state),
  canGenerateErrorReport: userHasRight(FORM_ERROR_REPORT_CREATE)(state),
});

export default withRouter(connect(mapStateToProps)(PhoenixFormsListDetails));
